import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
    Box,
    Container,
    Paper,
    Typography,
    Button,
    CircularProgress,
    Alert,
    Avatar,
    Divider,
} from '@mui/material';
import { validateCommunityInvitation, acceptCommunityInvitation } from 'api';
import { useAuth } from 'hooks/useAuth';
import routes from 'config/routes';

const CommunityInvitationContainer = () => {
    const { token } = useParams();
    const navigate = useNavigate();
    const { isAuthenticated, user } = useAuth();
    
    const [loading, setLoading] = useState(true);
    const [accepting, setAccepting] = useState(false);
    const [error, setError] = useState(null);
    const [invitation, setInvitation] = useState(null);
    const [success, setSuccess] = useState(false);

    useEffect(() => {
        const validateInvitation = async () => {
            try {
                setLoading(true);
                setError(null);
                
                const response = await validateCommunityInvitation(token);
                
                if (response.data?.valid) {
                    setInvitation(response.data);
                } else {
                    setError(response.data?.error || 'Invalid invitation');
                }
            } catch (err) {
                console.error('Error validating invitation:', err);
                const errorMessage = err.response?.data?.error || 'Failed to validate invitation';
                setError(errorMessage);
            } finally {
                setLoading(false);
            }
        };

        if (token) {
            validateInvitation();
        }
    }, [token]);

    const handleAccept = async () => {
        if (!isAuthenticated) {
            // Redirect to login with return URL
            navigate(`${routes.login}?redirect=/community-invitation/${token}`);
            return;
        }

        try {
            setAccepting(true);
            setError(null);
            
            const response = await acceptCommunityInvitation(token);
            
            if (response.data?.success) {
                setSuccess(true);
                // Redirect to community after a short delay
                setTimeout(() => {
                    navigate(`/community/${response.data.community_slug}`);
                }, 2000);
            } else {
                setError(response.data?.error || 'Failed to accept invitation');
            }
        } catch (err) {
            console.error('Error accepting invitation:', err);
            const errorMessage = err.response?.data?.error || 'Failed to accept invitation';
            setError(errorMessage);
        } finally {
            setAccepting(false);
        }
    };

    const getErrorMessage = (errorCode) => {
        const messages = {
            'token_required': 'Invalid invitation link.',
            'invitation_not_found': 'This invitation does not exist or has been cancelled.',
            'invitation_expired': 'This invitation has expired. Please request a new one.',
            'invitation_already_used': 'This invitation has already been used.',
            'authentication_required': 'Please log in to accept this invitation.',
            'already_member': 'You are already a member of this community!',
        };
        return messages[errorCode] || errorCode;
    };

    if (loading) {
        return (
            <Container maxWidth="sm">
                <Box sx={{ 
                    display: 'flex', 
                    justifyContent: 'center', 
                    alignItems: 'center', 
                    minHeight: '60vh' 
                }}>
                    <CircularProgress />
                </Box>
            </Container>
        );
    }

    if (error && !invitation) {
        return (
            <Container maxWidth="sm">
                <Box sx={{ mt: 8 }}>
                    <Paper elevation={3} sx={{ p: 4, textAlign: 'center' }}>
                        <Typography variant="h5" color="error" gutterBottom>
                            Invitation Error
                        </Typography>
                        <Alert severity="error" sx={{ mt: 2 }}>
                            {getErrorMessage(error)}
                        </Alert>
                        <Button 
                            variant="contained" 
                            sx={{ mt: 3 }}
                            onClick={() => navigate(routes.communities)}
                        >
                            Browse Communities
                        </Button>
                    </Paper>
                </Box>
            </Container>
        );
    }

    if (success) {
        return (
            <Container maxWidth="sm">
                <Box sx={{ mt: 8 }}>
                    <Paper elevation={3} sx={{ p: 4, textAlign: 'center' }}>
                        <Typography variant="h5" color="success.main" gutterBottom>
                            🎉 Welcome to {invitation?.community?.name}!
                        </Typography>
                        <Typography variant="body1" sx={{ mt: 2 }}>
                            You have successfully joined the community.
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                            Redirecting you to the community page...
                        </Typography>
                        <CircularProgress size={24} sx={{ mt: 2 }} />
                    </Paper>
                </Box>
            </Container>
        );
    }

    return (
        <Container maxWidth="sm">
            <Box sx={{ mt: 8 }}>
                <Paper elevation={3} sx={{ p: 4 }}>
                    {/* Community Header */}
                    <Box sx={{ textAlign: 'center', mb: 3 }}>
                        {invitation?.community?.image && (
                            <Avatar
                                src={invitation.community.image}
                                alt={invitation.community.name}
                                sx={{ 
                                    width: 100, 
                                    height: 100, 
                                    mx: 'auto',
                                    mb: 2,
                                    border: '3px solid',
                                    borderColor: 'primary.main'
                                }}
                            />
                        )}
                        <Typography variant="h4" gutterBottom>
                            You're Invited!
                        </Typography>
                        <Typography variant="h6" color="primary">
                            {invitation?.community?.name}
                        </Typography>
                    </Box>

                    <Divider sx={{ my: 3 }} />

                    {/* Invitation Details */}
                    <Box sx={{ mb: 3 }}>
                        <Typography variant="body1" gutterBottom>
                            <strong>{invitation?.inviter_name}</strong> has invited you to join this community.
                        </Typography>
                        
                        {invitation?.personal_message && (
                            <Box sx={{ 
                                mt: 2, 
                                p: 2, 
                                bgcolor: 'grey.100', 
                                borderRadius: 1,
                                fontStyle: 'italic'
                            }}>
                                "{invitation.personal_message}"
                            </Box>
                        )}

                        <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                            You will join as: <strong>{invitation?.role_display}</strong>
                        </Typography>

                        {invitation?.community?.description && (
                            <Typography variant="body2" sx={{ mt: 2 }}>
                                {invitation.community.description}
                            </Typography>
                        )}
                    </Box>

                    {error && (
                        <Alert severity="error" sx={{ mb: 2 }}>
                            {getErrorMessage(error)}
                        </Alert>
                    )}

                    {/* Action Buttons */}
                    <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center' }}>
                        {isAuthenticated ? (
                            <Button
                                variant="contained"
                                size="large"
                                onClick={handleAccept}
                                disabled={accepting}
                                sx={{ minWidth: 200 }}
                            >
                                {accepting ? (
                                    <>
                                        <CircularProgress size={20} sx={{ mr: 1 }} />
                                        Joining...
                                    </>
                                ) : (
                                    'Accept Invitation'
                                )}
                            </Button>
                        ) : (
                            <Box sx={{ textAlign: 'center' }}>
                                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                                    Please log in or sign up to accept this invitation
                                </Typography>
                                <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center' }}>
                                    <Button
                                        variant="contained"
                                        onClick={() => navigate(`${routes.login}?redirect=/community-invitation/${token}`)}
                                    >
                                        Log In
                                    </Button>
                                    <Button
                                        variant="outlined"
                                        onClick={() => navigate(`${routes.signup}?redirect=/community-invitation/${token}`)}
                                    >
                                        Sign Up
                                    </Button>
                                </Box>
                            </Box>
                        )}
                    </Box>

                    <Typography 
                        variant="caption" 
                        color="text.secondary" 
                        sx={{ display: 'block', textAlign: 'center', mt: 3 }}
                    >
                        This invitation is for: {invitation?.email}
                    </Typography>
                </Paper>
            </Box>
        </Container>
    );
};

export default CommunityInvitationContainer;
